import React, { useState, useEffect } from 'react';
import { ApiURLs } from '../../constant/ApiUrls';
import { fetchData } from '../../services/api';
import { pickOutline, outlineBounds } from './outlines';

/*
  Heat map. Two maps of the same country, side by side:
  where sellers are, and where users are, so the two can be matched.

  This site only. No figures from the other island - they are separate
  businesses with separate budgets, and they record location at different grain
  anyway, so mixing them tells you nothing you can act on.

  THE NUMBERS ARE OUR OWN, THE COASTLINE IS NOT.
  Each area is still placed at the average position of its own adverts, so every
  figure on this screen comes from our data. What sits underneath is a real
  coastline - Chris's note was "I can't see the Irish outline", and he was right
  that bubbles in white space do not read as a map. See outlines.jsx: it is
  embedded, public domain, and loads nothing at runtime.

  An area we hold no coordinate for is listed beside the map rather than dropped
  or guessed at - a dot in the wrong county would be worse than an honest gap.
*/

const PAD = 26;

// Simple equirectangular projection, good enough at the size of one country and
// honest about what it is. Longitude is squeezed by cos(latitude) so Ireland
// does not come out stretched sideways.
//
// The frame is fitted to the COASTLINE and the points together, not to the
// points alone. Fitted to the points, the island would be cropped to whichever
// counties happen to have stock this week, and the map would change shape every
// time a dealer was added. Including the points as well means a position just
// off the drawn coast is still visible rather than silently outside the frame.
const project = (areas, w, h, outline) => {
  const pts = areas.filter((a) => a.lat != null && a.lng != null);
  if (!pts.length && !outline) return { place: () => null, empty: true };
  const lats = pts.map((p) => p.lat);
  const lngs = pts.map((p) => p.lng);
  if (outline) {
    const [ox0, oy0, ox1, oy1] = outlineBounds(outline.rings);
    lngs.push(ox0, ox1);
    lats.push(oy0, oy1);
  }
  const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  const k = Math.cos((midLat * Math.PI) / 180);
  const xs = lngs.map((l) => l * k);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  const [y0, y1] = [Math.min(...lats), Math.max(...lats)];
  const spanX = (x1 - x0) || 1;
  const spanY = (y1 - y0) || 1;
  // One scale for both axes, so the country keeps its proportions.
  const scale = Math.min((w - PAD * 2) / spanX, (h - PAD * 2) / spanY);
  const offX = (w - spanX * scale) / 2;
  const offY = (h - spanY * scale) / 2;
  return {
    empty: false,
    place: (a) => {
      if (a.lat == null || a.lng == null) return null;
      return {
        x: offX + (a.lng * k - x0) * scale,
        // Latitude grows northwards, the screen grows downwards.
        y: offY + (y1 - a.lat) * scale,
      };
    },
  };
};

const shade = (t) => {
  const mix = (a, b) => Math.round(a + (b - a) * t);
  return `rgb(${mix(190, 12)}, ${mix(214, 58)}, ${mix(235, 110)})`;
};

const MapPanel = ({ map, grain }) => {
  const [hover, setHover] = useState(null);
  const W = 430;
  const H = 470;
  const outline = pickOutline(map.areas);
  const proj = project(map.areas, W, H, outline);
  const max = map.areas.reduce((m, a) => Math.max(m, a.count), 0) || 1;
  const placed = map.areas.filter((a) => a.lat != null && a.lng != null);
  const missing = map.areas.filter((a) => a.lat == null || a.lng == null);

  return (
    <div style={{ flex: '1 1 440px', minWidth: 380 }}>
      <div style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>{map.title}</h3>
        <div style={{ fontSize: 13, color: '#667085' }}>
          {map.subtitle} &middot; {map.total.toLocaleString()} in total
        </div>
      </div>

      <div style={{
        background: '#fff', borderRadius: 10, border: '1px solid #eceff3',
        position: 'relative', height: H, overflow: 'hidden',
      }}>
        {proj.empty ? (
          <div style={{ padding: 24, color: '#888', fontSize: 13 }}>
            Nothing with a position to draw yet.
          </div>
        ) : (
          <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
            {/* The land, drawn first so every bubble sits on top of it. Pale on
                purpose: this is the backdrop, the data is the subject. */}
            {outline && outline.rings.map((r, i) => {
              const d = r
                .map((c, j) => {
                  const p = proj.place({ lng: c[0], lat: c[1] });
                  return `${j ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`;
                })
                .join(' ');
              return (
                <path key={`land-${i}`} d={`${d} Z`}
                  fill="#e4ebf2" stroke="#b7c6d6" strokeWidth={0.9} />
              );
            })}
            {placed.map((a) => {
              const p = proj.place(a);
              if (!p) return null;
              const t = a.count / max;
              // Area of the circle carries the number, not the radius, so a
              // county with ten times the adverts does not look a hundred times
              // bigger.
              const r = 7 + Math.sqrt(t) * 30;
              const on = hover === a.area;
              return (
                <g key={a.area}
                  onMouseEnter={() => setHover(a.area)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'default' }}>
                  <circle cx={p.x} cy={p.y} r={r}
                    fill={shade(t)} fillOpacity={on ? 0.95 : 0.72}
                    stroke={on ? '#0b2d4d' : '#ffffff'} strokeWidth={on ? 2 : 1} />
                  {(t > 0.18 || on) && (
                    <text x={p.x} y={p.y + 4} textAnchor="middle"
                      style={{ fontSize: 11, fontWeight: 700, fill: t > 0.45 ? '#fff' : '#123' }}>
                      {a.count.toLocaleString()}
                    </text>
                  )}
                </g>
              );
            })}
            {placed.map((a) => {
              const p = proj.place(a);
              if (!p) return null;
              const t = a.count / max;
              const r = 7 + Math.sqrt(t) * 30;
              if (t <= 0.18 && hover !== a.area) return null;
              return (
                <text key={`l-${a.area}`} x={p.x} y={p.y - r - 5} textAnchor="middle"
                  style={{ fontSize: 11, fontWeight: 600, fill: '#0b2d4d' }}>{a.area}</text>
              );
            })}
          </svg>
        )}
      </div>

      {missing.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#7a5c00', background: '#fff8e6',
          border: '1px solid #f0dca8', borderRadius: 6, padding: '7px 10px' }}>
          Not on the map, no position held:{' '}
          {missing.map((m) => `${m.area} (${m.count})`).join(', ')}
        </div>
      )}

      <div style={{ marginTop: 10, background: '#fff', borderRadius: 10, border: '1px solid #eceff3', overflow: 'hidden' }}>
        {map.areas.slice(0, 12).map((a) => (
          <div key={a.area} style={{
            display: 'grid', gridTemplateColumns: '1fr 60px 60px 64px',
            padding: '7px 12px', fontSize: 12.5, alignItems: 'center',
            borderBottom: '1px solid #f7f8fa',
            background: hover === a.area ? '#f2f7fd' : '#fff',
          }}
            onMouseEnter={() => setHover(a.area)} onMouseLeave={() => setHover(null)}>
            <div style={{ fontWeight: 600 }}>{a.area}</div>
            <div style={{ textAlign: 'right', fontWeight: 700 }}>{a.count.toLocaleString()}</div>
            <div style={{ textAlign: 'right', color: '#888' }}>{a.dealer.toLocaleString()}</div>
            <div style={{
              textAlign: 'right', fontWeight: a.private > 0 ? 700 : 400,
              color: a.private > 0 ? '#137a3f' : '#c4c4c4',
            }}>{a.private.toLocaleString()}</div>
          </div>
        ))}
        <div style={{ padding: '6px 12px', fontSize: 11, color: '#999' }}>
          columns: {grain} &middot; total &middot; dealer &middot; private
        </div>
      </div>
    </div>
  );
};

const AdvertiserMap = () => {
  const [data, setData] = useState(null);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchData(ApiURLs.ADVERTISER_MAP);
        if (cancelled) return;
        if (res && res.status === 1 && Array.isArray(res.maps)) setData(res);
        else setNotice((res && res.message) ? `Could not load: ${res.message}` : 'Could not load the heat map.');
      } catch (e) {
        if (!cancelled) setNotice('Could not load the heat map.');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ padding: 24, background: '#f7f8fa', minHeight: '100vh' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 26, fontWeight: 700, color: '#1a1a2e' }}>Heat map</h2>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: '#667085', maxWidth: 820 }}>
        {data ? `${data.site} only.` : ''} Sellers on the left, users on the right, by {data ? data.grain : 'area'},
        so the two can be read against each other. Circle size and colour are the count.
      </p>

      {notice ? (
        <div style={{ background: '#fff8e6', border: '1px solid #f0dca8', color: '#7a5c00',
          padding: '10px 14px', borderRadius: 8, marginBottom: 18, fontSize: 13 }}>{notice}</div>
      ) : null}

      {loading ? <div style={{ color: '#888', fontSize: 14 }}>Loading…</div>
        : !data ? null : (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {data.maps.map((m) => <MapPanel key={m.kind} map={m} grain={data.grain} />)}
          </div>
        )}
    </div>
  );
};

export default AdvertiserMap;
